import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild, type OnChanges, type OnDestroy, type OnInit, type SimpleChanges } from '@angular/core';
import { StandardDropdownsService } from '@zaoblako/web/core/services/standard-dropdowns.service';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { STANDARD_DROPDOWN_VIRTUAL_ITEM_SIZE, StandardDropdownEngine } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-dropdown.engine';
import { StandardDropdownItemComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-dropdown-item/standard-dropdown-item.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { StandardDropdownItemPipe } from '@zaoblako/web/shared/pipes/standard-dropdown-item.pipe';
import type { StandardDropdownDescriptorInterface } from '@zaoblako/shared/common/dtos/standard-dropdown/standard-dropdown-descriptor.dto';

import { isNil } from 'lodash';
import { SkeletonModule } from 'primeng/skeleton';

const PANEL_OPEN_DELAY = 100;
const PANEL_SCROLL_BIND_ATTEMPTS = 8;

@Component({
  selector: 'crm-standard-multi-select',
  standalone: true,
  templateUrl: './standard-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MultiSelectComponent,
    SpinnerComponent,
    StandardDropdownItemComponent,
    StandardDropdownItemPipe,
    SkeletonModule
  ]
})
export class StandardMultiSelectComponent<T extends Record<string, any> = Record<string, any>> implements OnInit, OnChanges, OnDestroy {
  @Input() public customDescriptor?: StandardDropdownDescriptorInterface;
  @Input() public descriptorApiUrl?: string;
  @Input() public noCacheApiDescriptor = false;
  @Input() public isError = false;
  @Input() public class?: string;
  @Input() public styleClass?: string;
  @Input() public itemStyleClass?: string;
  @Input() public disabled?: boolean;
  @Input() public appendTo?: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;
  @Input() public labelInputText = '';
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public singleLine?: boolean;
  @Input() public virtualScroll = false;
  @Input() public virtualScrollItemSize = STANDARD_DROPDOWN_VIRTUAL_ITEM_SIZE;
  @Input() public additionalApiParams?: Record<string, any>;

  @Output() public selectedValuesChange = new EventEmitter<T[]>();
  @Output() public selectedKeysChange = new EventEmitter<Array<string | number>>();

  @ViewChild('multiSelectComponent') public multiSelectComponent?: MultiSelectComponent;

  public appliedValues: T[] = [];
  public filterValue = '';
  public readonly engine: StandardDropdownEngine<T>;

  private panelScrollTimer?: ReturnType<typeof setTimeout>;

  public constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    standardDropdownsService: StandardDropdownsService
  ) {
    this.engine = new StandardDropdownEngine<T>(standardDropdownsService, () => this.changeDetectorRef.markForCheck());
  }

  @Input() public set selectedValues(values: T[] | null | undefined) {
    if (isNil(values)) {
      return;
    }
    this.appliedValues = values;
    if (this.engine.ensurePresent(values)) {
      this.changeDetectorRef.markForCheck();
    }
    this.engine.preloadMissing(values);
  }

  @Input() public set selectedKeys(keys: Array<string | number> | null | undefined) {
    this.engine.setKeys(keys);
  }

  public ngOnInit(): void {
    this.engine.onPageReplaced = () => {
      if (this.appliedValues.length) {
        this.engine.ensurePresent(this.appliedValues);
        this.engine.preloadMissing(this.appliedValues);
      }
    };
    this.engine.onKeysResolved = (items) => {
      const currentKeys = this.engine.keysOf(this.appliedValues);
      const nextKeys = this.engine.keysOf(items);
      const sameSelection = currentKeys.length === nextKeys.length && currentKeys.every((key, index) => key === nextKeys[index]);
      if (sameSelection) {
        return;
      }
      this.appliedValues = items;
      this.changeDetectorRef.markForCheck();
    };
    this.engine.configure(this.source());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const sourceChanged = ['customDescriptor', 'descriptorApiUrl', 'additionalApiParams', 'noCacheApiDescriptor'].some(
      (key) => changes[key] && !changes[key].isFirstChange()
    );
    if (sourceChanged) {
      this.engine.configure(this.source());
    }
  }

  public ngOnDestroy(): void {
    this.clearPanelScrollTimer();
    this.engine.destroy();
  }

  public onShowPanel = (): void => {
    this.schedulePanelScrollBind(0);
  };

  public onHidePanel = (): void => {
    this.clearPanelScrollTimer();
    this.engine.unbindPanelScroll();
  };

  private schedulePanelScrollBind = (attempt: number): void => {
    this.clearPanelScrollTimer();
    this.panelScrollTimer = setTimeout(() => {
      const element = this.panelScrollElement();
      if (!element) {
        if (attempt < PANEL_SCROLL_BIND_ATTEMPTS) {
          this.schedulePanelScrollBind(attempt + 1);
        }
        return;
      }
      this.engine.bindPanelScroll(element);
    }, PANEL_OPEN_DELAY);
  };

  private panelScrollElement = (): HTMLElement | null => {
    const multiSelect = this.multiSelectComponent?.multiSelect;
    const overlay = multiSelect?.overlayViewChild?.overlayViewChild?.nativeElement as HTMLElement | undefined;
    if (this.virtualScroll) {
      return overlay?.querySelector('.p-scroller') ?? null;
    }
    if (multiSelect?.itemsWrapper instanceof HTMLElement) {
      return multiSelect.itemsWrapper;
    }
    return overlay?.querySelector('.p-multiselect-items-wrapper') ?? null;
  };

  private clearPanelScrollTimer = (): void => {
    if (this.panelScrollTimer) {
      clearTimeout(this.panelScrollTimer);
      this.panelScrollTimer = undefined;
    }
  };

  public search = (filter: string): void => {
    this.filterValue = filter;
    this.changeDetectorRef.markForCheck();
    this.engine.search(filter);
  };

  public onSelect = (values: T[] | null): void => {
    const next = values ?? [];
    if (!next.length && this.engine.shouldIgnoreClearedSelection) {
      return;
    }
    if (this.engine.matchesResolvedKeys(next)) {
      this.appliedValues = next;
      return;
    }

    this.appliedValues = next;
    this.selectedValuesChange.emit(next);
    this.selectedKeysChange.emit(this.engine.keysOf(next));
    this.engine.ensurePresent(next);
    this.engine.preloadMissing(next);
  };

  private source = () => ({
    customDescriptor: this.customDescriptor,
    descriptorApiUrl: this.descriptorApiUrl,
    noCacheApiDescriptor: this.noCacheApiDescriptor,
    additionalApiParams: this.additionalApiParams
  });
}
